import 'vue-multiselect/dist/vue-multiselect.min.css';
import Multiselect from 'vue-multiselect';

import debounce from 'lodash.debounce';
import DefaultTools from '@/components/Includes/DefaultTools/DefaultTools.vue';
import Pagination from "@/components/Includes/Pagination/Pagination.vue";
import SelectionColumnsInTable from "@/components/Includes/SelectionColumnsInTable/SelectionColumnsInTable.vue";
import ITableCol from "@/interfaces/ITableCol";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import transformDateFields from "@/libs/date/transformDateFields";
import getParamQuery from "@/libs/getParamQuery";
import getQueryData from "@/libs/getQueryData";
import getTableColumns from "@/libs/getTableColumns";
import {ACCESS_LEVEL_CREATE} from "@/libs/userAbility/userAbilityRoles/rolesAccessLevel";
import {VPN_CREATE} from "@/libs/userAbility/userAbilityRoles/rolesVpn";
import {Component, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import Loader from '@/components/Includes/Loader/Loader.vue';
import TableEmpty from '@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue';
import ICity from '@/interfaces/cities/ICity';
import ICountry from '@/interfaces/countries/ICountry';
import IDataCenter from '@/interfaces/dataCenter/IDataCenter';
import IAccessLevel from '@/interfaces/IAccessLevel';
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import IVpn from '@/interfaces/vpns/IVpn';
import searchByTable from '@/libs/searchQuery/searchByTable';

@Component({
    name: 'VpnList',
    components: {
        DefaultTools,
        Pagination,
        SelectionColumnsInTable,
        TableEmpty,
        Loader,
        Multiselect,
    }
})
export default class VpnList extends Vue {
    @Action private vpnsList!: (query: IPayloadQuery) => number;
    @Action private vpnRemove!: (id: number) => string;
    @Action private accessLevelsList!: (query: IPayloadQuery) => Promise<void>;
    @Action private dataCentersList!: (query: IPayloadQuery) => Promise<void>;
    @Action private countriesList!: (query: IPayloadQuery) => Promise<void>;
    @Action private citiesList!: (query: IPayloadQuery) => Promise<void>;

    @Getter private getVPNs!: () => IVpn[];
    @Getter private getAccessLevels!: () => IAccessLevel[];
    @Getter private getDataCenters!: () => IDataCenter[];
    @Getter private getCountries!: () => ICountry[];
    @Getter private getCities!: () => ICity[];

    private relationsLoaded = false
    private loading = false;
    private searchText = ''
    private filters: {
        country: IMultiSelectItem[];
        active: boolean | null;
        alive: boolean | null;
    } = {
        country: [],
        active: null,
        alive: null,
    }

    private vpns: IVpn[] = [];

    private selectedTableColumns: IMultiSelectItem[] = [];

    // Begin Config for Pagination
    private totalVpns: number | void | null = null;
    private currentPage: number = 1;
    private selectedLimit: string = '100';
    private forceUpdateVPNsKey = Date.now()

    private onSearchVpns!: ReturnType<typeof debounce>

    private get switcherSelectOptions() {
        return [
            {
                text: 'Any',
                value: null
            },
            {
                text: 'On',
                value: true
            },
            {
                text: 'Off',
                value: false
            }
        ]
    }

    private get countryOptions() {
        return this.getCountries().map(({ id: value, name: label }) => ({
            value,
            label
        }))
    }

    private get showPagination() {
        return this.totalVpns;
    }

    // End Config for Pagination

    private get accessVpnCreate(): string {
        return VPN_CREATE;
    }

    private get tableItems() {
        return this.vpns;
    }

    private get tableColumns(): ITableCol[] | undefined {
        if (this.tableItems) {
            return getTableColumns(this.tableItems, true, [], {name: 'delete', index: -3})
        }
    };

    private get fields() {
        if (this.tableColumns) {
            const columnsIds = this.selectedTableColumns.map((el: IMultiSelectItem) => el.value);
            return this.tableColumns.filter((el: any) => columnsIds.includes(el.key));
        } else {
            return this.tableColumns;
        }
    }

    private getBadge(type: string) {
        switch (type) {
            case 'freedom':
                return 'success';
            case 'trial':
                return 'danger';
            default:
                return 'primary';
        }
    }

    private updateLimit(newLimit: string): void {
        this.selectedLimit = newLimit;
    }

    private vpnDetail(vpn: any) {
        this.$router.push({
            name: 'vpn_detail',
            params: {id: vpn.id, vpnData: vpn}
        });
    }

    private async deleteVpn(id: number, index: number) {
        try {
            const approveRemove = confirm('Вы уверены что хотите удалить VPN?');

            if(approveRemove){
                const removeStatus = await this.vpnRemove(Number(id));

                if (removeStatus === 'deleted') {
                    this.vpns.splice(index, 1);

                    this.$notify({
                        group: 'notice',
                        title: 'VPN',
                        text: 'Deleted Successfully',
                        type: 'success'
                    });
                }
            }
        } catch (error) {
            throw new Error(error);
        }
    }

    // Begin Query Logic
    private get payloadQuery(): IPayloadQuery {
        return {
            ...getParamQuery(this.$route),
            metaData: true
        }
    }

    private queryValue() {
        if (this.payloadQuery.query && this.payloadQuery.query.length > 0) {
            this.readQueryValue();
        } else {
            this.writeQueryValue();
            this.readQueryValue();
        }
    }

    private readQueryValue() {
        const queryData = getQueryData(this.$route.query);

        queryData.map(({key, value}) => {
            switch (key) {
                case 'page': {
                    this.currentPage = value;
                    break;
                }
                case 'limit': {
                    this.selectedLimit = value;
                    break;
                }
                default:
                    break;
            }
        });
    }

    @Watch('selectedLimit')
    @Watch('currentPage')
    private writeQueryValue() {
        const url = new URL(window.location.href)

        url.searchParams.set('page', this.currentPage.toString());
        url.searchParams.delete('limit');

        if (url.toString() === window.location.href) {
            return
        }

        this.$router.replace(`${url.pathname}${url.search}`);
    }

    // End Query Logic

    // Begin Realtions logic
    private findField(data: any, id: number) {
        return data.find((el: any) => el.id === id);
    }

    private vpnAccessLevels(): void {
        this.vpns.forEach((vpn, index) => {
            const fullRelationData = this.findField(this.getAccessLevels(), vpn.accessLevel.id);
            this.vpns[index].accessLevel = fullRelationData ? fullRelationData.name : vpn.accessLevel
        });
    }

    private vpnDataCenter(): void {
        this.vpns.forEach((vpn, index) => {
            const fullRelationData = this.findField(this.getDataCenters(), vpn.dataCenter.id);
            this.vpns[index].dataCenter = fullRelationData ? fullRelationData.name : vpn.dataCenter
        });
    }

    private vpnCountry(): void {
        this.vpns.forEach((vpn, index) => {
            const fullRelationData = this.findField(this.getCountries(), vpn.country.id);
            this.vpns[index].country = fullRelationData ? fullRelationData.name : vpn.country
        });
    }

    private vpnCity(): void {
        this.vpns.forEach((vpn, index) => {
            const fullRelationData = this.findField(this.getCities(), vpn.city.id);
            this.vpns[index].city = fullRelationData ? fullRelationData.name : vpn.city
        });
    }

    private async getRelations(): Promise<void> {
        if (this.relationsLoaded) {
            return
        }

        await Promise.allSettled([
            this.accessLevelsList({query: '?pagination=false'}),
            this.dataCentersList({query: '?pagination=false'}),
            this.citiesList({query: '?pagination=false'}),
            this.countriesList({query: '?pagination=false'})
        ])

        this.relationsLoaded = true
    }

    // End Realtions logic

    // Begin Transform Data for Form Campaign
    private checkDateFields() {
        const dateFields: string[] = ['createdAt', 'updatedAt'];
        transformDateFields(dateFields, this.vpns)
    }

    // End Transform Data for Form Campaign
    private async loadVpns() {
        if (this.loading) {
            return
        }

        const searchText = this.searchText || ''

        let host = ''
        let ip = ''

        if (/^[0-9.]+?$/.test(searchText)) {
            ip = searchText
        }
        else {
            host = searchText
        }

        const country = this.filters.country.map(({ value }) => value)
        const alive = this.filters.alive
        const active = this.filters.active

        try {
            this.loading = true;
            this.queryValue();
            this.totalVpns = await this.vpnsList({
                ...this.payloadQuery,
                params: {
                    limit: this.selectedLimit,
                    country: country.length ? country : undefined,
                    host: host || undefined,
                    ip: ip || undefined,
                    alive: alive !== null ? alive: undefined,
                    active: active !== null ? active: undefined,
                }
            });

            if (
                searchText === this.searchText &&
                this.filters.country.length === country.length &&
                this.filters.active === active &&
                this.filters.alive === alive
              ) {
                await this.getRelations();

                this.vpns = this.getVPNs();
                this.checkDateFields();
    
                this.vpnAccessLevels();
                this.vpnDataCenter();
                this.vpnCity();
                this.vpnCountry();
                this.loading = false;
            }
            else {
                this.loading = false;
                this.loadVpns()
            }
        } finally {
            this.loading = false;
        }

    }

    private created() {
        this.onSearchVpns = debounce((searchText: string) => {
            this.searchText = searchText.trim()
        }, 700, {
          maxWait: 700,
        });
    }

    private mounted() {    
        this.$watch(
            () => {
                return [
                    this.selectedLimit,
                    this.searchText,
                    this.filters.country.length,
                    this.filters.active,
                    this.filters.alive,
                ].join("-");
            },
            () => {
              this.currentPage = 1
              this.forceUpdateVPNsKey++
            },
            {
              immediate: true,
            }
        );

        this.$watch(
            () => {
                return [this.currentPage,  this.forceUpdateVPNsKey].join('-')
            },
            () => {
                this.loadVpns()
            },
            {
              immediate: true,
            }
        );
    }
}
